import React, { useMemo, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { formatCurrency, getCurrencySymbol } from '../charts/chartTheme';
import { TrendingUp, TrendingDown, Wallet, PieChart, Activity, Plus, X } from 'lucide-react';
import { format, parseISO, eachDayOfInterval, startOfWeek, endOfWeek, startOfMonth, endOfMonth, getWeek, startOfDay } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { BoardBottomDrawer } from '../BoardBottomDrawer';
import { AIVerdictModule } from '../AIVerdictModule';
import { useBoardStore } from '../../../../stores/boardStore';

interface PerformancePageProps {
}

const getSupportedKpis = (t: any): Record<string, { title: string; icon: any; color: string; isCurrency: boolean; description: string; unit?: string; precision?: number }> => ({
    grossSales: { title: t("pages.performance.kpis.grossSales.title"), icon: TrendingUp, color: "bg-emerald-500", isCurrency: true, description: t("pages.performance.kpis.grossSales.desc") },
    netSales: { title: t("pages.performance.kpis.netSales.title"), icon: TrendingUp, color: "bg-green-600", isCurrency: true, description: t("pages.performance.kpis.netSales.desc") },
    revenue: { title: t("pages.performance.kpis.revenue.title"), icon: TrendingUp, color: "bg-teal-500", isCurrency: true, description: t("pages.performance.kpis.revenue.desc") },
    expenses: { title: t("pages.performance.kpis.expenses.title"), icon: TrendingDown, color: "bg-rose-500", isCurrency: true, description: t("pages.performance.kpis.expenses.desc") },
    net: { title: t("pages.performance.kpis.net.title"), icon: Wallet, color: "bg-blue-500", isCurrency: true, description: t("pages.performance.kpis.net.desc") },
    margin: { title: t("pages.performance.kpis.margin.title"), icon: Activity, color: "bg-amber-500", isCurrency: false, unit: "%", description: t("pages.performance.kpis.margin.desc") },
    orders: { title: t("pages.performance.kpis.orders.title"), icon: Activity, color: "bg-orange-500", isCurrency: false, precision: 0, description: t("pages.performance.kpis.orders.desc") },
    aov: { title: t("pages.performance.kpis.aov.title"), icon: Wallet, color: "bg-indigo-500", isCurrency: true, description: t("pages.performance.kpis.aov.desc") },
    discountRate: { title: t("pages.performance.kpis.discountRate.title"), icon: TrendingDown, color: "bg-purple-500", isCurrency: false, unit: "%", description: t("pages.performance.kpis.discountRate.desc") },
    returnRate: { title: t("pages.performance.kpis.returnRate.title"), icon: TrendingDown, color: "bg-red-500", isCurrency: false, unit: "%", description: t("pages.performance.kpis.returnRate.desc") },
    shippingRevenue: { title: t("pages.performance.kpis.shippingRevenue.title"), icon: Wallet, color: "bg-yellow-500", isCurrency: true, description: t("pages.performance.kpis.shippingRevenue.desc") },
    transactionFees: { title: t("pages.performance.kpis.transactionFees.title"), icon: TrendingDown, color: "bg-amber-600", isCurrency: true, description: t("pages.performance.kpis.transactionFees.desc") },
    txFeePercent: { title: t("pages.performance.kpis.txFeePercent.title"), icon: PieChart, color: "bg-amber-500", isCurrency: false, unit: "%", description: t("pages.performance.kpis.txFeePercent.desc") },
    customers: { title: t("pages.performance.kpis.customers.title"), icon: Activity, color: "bg-violet-500", isCurrency: false, precision: 0, description: t("pages.performance.kpis.customers.desc") },
    avgRevenuePerCustomer: { title: t("pages.performance.kpis.avgRevenuePerCustomer.title"), icon: TrendingUp, color: "bg-fuchsia-500", isCurrency: true, description: t("pages.performance.kpis.avgRevenuePerCustomer.desc") },
    roas: { title: t("pages.performance.kpis.roas.title"), icon: Activity, color: "bg-indigo-600", isCurrency: false, unit: "x", description: t("pages.performance.kpis.roas.desc") },
    cac: { title: t("pages.performance.kpis.cac.title"), icon: TrendingDown, color: "bg-pink-500", isCurrency: true, description: t("pages.performance.kpis.cac.desc") },
    adSpendPercent: { title: t("pages.performance.kpis.adSpendPercent.title"), icon: PieChart, color: "bg-violet-600", isCurrency: false, unit: "%", description: t("pages.performance.kpis.adSpendPercent.desc") }
});

export const PerformancePage: React.FC<PerformancePageProps> = () => {
    const { t } = useTranslation("common");
    const { finance, currencySettings, dateRange } = useBoardChartData();
    const boardId = useBoardStore(state => state.boardId);
    const activePageId = useBoardStore(state => state.activePageId);
    const currencySymbol = getCurrencySymbol(currencySettings?.default);

    const [visibleKpis, setVisibleKpis] = React.useState<string[]>([
        'orders', 'roas', 'aov', 'cac', 'margin'
    ]);

    const [viewMode, setViewMode] = React.useState<'weeks' | 'months' | 'all'>('months');
    const [selectedPeriodIndex, setSelectedPeriodIndex] = React.useState(0);
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const [isAiMobileExpanded, setIsAiMobileExpanded] = useState(false);
    const [menuAlign, setMenuAlign] = React.useState<'left' | 'right'>('left');
    const [hoveredKpi, setHoveredKpi] = React.useState<string | null>(null);
    const [hoverSide, setHoverSide] = React.useState<'left' | 'right'>('right');
    const [hoverOffset, setHoverOffset] = React.useState(0);
    const addButtonRef = React.useRef<HTMLButtonElement | null>(null);
    const menuRef = React.useRef<HTMLDivElement | null>(null);

    const SUPPORTED_KPIS = useMemo(() => getSupportedKpis(t), [t]);
    const { i18n: i18nInstance } = useTranslation();
    const currentLocale = i18nInstance.language === 'fr' ? fr : enUS;

    const { periods, tableData } = useMemo(() => {
        const txs = finance?.transactions?.filter((tx: any) => tx.isActive !== false) || [];

        const start = dateRange.startDate ? startOfDay(dateRange.startDate) : startOfMonth(new Date());
        const end = dateRange.endDate ? startOfDay(dateRange.endDate) : endOfMonth(new Date());
        const intervalDays = eachDayOfInterval({ start, end });

        const generatedPeriods: { title: string, days: Date[] }[] = [];
        if (viewMode === 'weeks') {
            const weeksSet = new Set<string>();
            intervalDays.forEach(day => {
                const wStart = startOfWeek(day, { weekStartsOn: 1 });
                const weekStr = format(wStart, 'yyyy-MM-dd');
                if (!weeksSet.has(weekStr)) {
                    weeksSet.add(weekStr);
                    const weekNum = getWeek(day, { weekStartsOn: 1 });
                    const periodDays = eachDayOfInterval({
                        start: wStart,
                        end: endOfWeek(day, { weekStartsOn: 1 })
                    }).filter(d => d >= start && d <= end);
                    generatedPeriods.push({ title: t('charts.components.week_number', { number: weekNum, defaultValue: `Week ${weekNum}` }), days: periodDays });
                }
            });
        } else if (viewMode === 'months') {
            const monthsSet = new Set<string>();
            intervalDays.forEach(day => {
                const mStart = startOfMonth(day);
                const monthStr = format(mStart, 'yyyy-MM');
                if (!monthsSet.has(monthStr)) {
                    monthsSet.add(monthStr);
                    const periodDays = eachDayOfInterval({
                        start: mStart,
                        end: endOfMonth(day)
                    }).filter(d => d >= start && d <= end);
                    generatedPeriods.push({ title: format(day, 'MMMM yyyy', { locale: currentLocale }), days: periodDays });
                }
            });
        }

        const txMap: Record<string, any[]> = {};
        txs.forEach(tx => {
            const d = format(parseISO(tx.date), 'yyyy-MM-dd');
            if (!txMap[d]) txMap[d] = [];
            txMap[d].push(tx);
        });

        return { periods: generatedPeriods, tableData: txMap };
    }, [finance, dateRange, viewMode]);

    const metrics = useMemo(() => {
        const txs = finance?.transactions?.filter((tx: any) => tx.isActive !== false) || [];
        let revenue = 0, expenses = 0, grossSales = 0, discounts = 0, returns = 0, adSpend = 0;
        let shippingRevenue = 0, transactionFees = 0;
        const orderIds = new Set<string>();
        const customerIds = new Set<string>();

        txs.forEach(tx => {
            const amount = Number(tx.amount) || 0;
            const cat = (tx.category || '').toLowerCase();
            if (tx.type === 'REVENUE') {
                revenue += amount;
                if (cat === 'product_sale' || cat === 'sale') {
                    grossSales += amount;
                    if (tx.actor?.act) orderIds.add(`${tx.channelId}_${tx.actor.act}`);
                    if (tx.actor?.id) customerIds.add(`${tx.channelId}_${tx.actor.id}`);
                } else if (cat === 'shipping') {
                    shippingRevenue += amount;
                }
            } else if (tx.type === 'EXPENSE') {
                const abs = Math.abs(amount);
                expenses += abs;
                if (cat === 'discount') discounts += abs;
                else if (cat === 'product_return' || cat === 'partial_return' || cat === 'net_return') returns += abs;
                else if (cat === 'ad_spend' || cat === 'advertising' || cat === 'marketing') adSpend += abs;
                else if (cat === 'transaction_fee' || cat === 'processing_fee') transactionFees += abs;
            }
        });

        const orders = orderIds.size;
        const customers = customerIds.size;
        const net = revenue - expenses;

        return {
            grossSales,
            netSales: grossSales - discounts - returns,
            revenue,
            expenses,
            net,
            margin: revenue > 0 ? (net / revenue) * 100 : 0,
            orders,
            aov: orders > 0 ? grossSales / orders : 0,
            discountRate: grossSales > 0 ? (discounts / grossSales) * 100 : 0,
            returnRate: grossSales > 0 ? (returns / grossSales) * 100 : 0,
            shippingRevenue,
            transactionFees,
            txFeePercent: revenue > 0 ? (transactionFees / revenue) * 100 : 0,
            customers,
            avgRevenuePerCustomer: customers > 0 ? revenue / customers : 0,
            roas: adSpend > 0 ? revenue / adSpend : 0,
            cac: customers > 0 ? adSpend / customers : 0,
            adSpendPercent: revenue > 0 ? (adSpend / revenue) * 100 : 0,
        };
    }, [finance]);

    const activePeriod = useMemo(() => {
        if (viewMode === 'all') {
            const start = dateRange.startDate ? startOfDay(dateRange.startDate) : startOfMonth(new Date());
            const end = dateRange.endDate ? startOfDay(dateRange.endDate) : endOfMonth(new Date());
            return { title: t('charts.components.all', 'All'), days: eachDayOfInterval({ start, end }) };
        }
        return periods[selectedPeriodIndex] || { title: '', days: [] };
    }, [viewMode, periods, selectedPeriodIndex, dateRange]);

    const toggleKpi = (key: string) => {
        setVisibleKpis(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    };

    const KPICard = ({ id, title, value, unit = '', icon: Icon, color, isCurrency = true, precision = 2 }: any) => (
        <div className="group relative bg-white dark:bg-neutral-900 rounded-xl p-4 border border-neutral-200 dark:border-neutral-800 shadow-sm hover:shadow-md transition-all duration-300 animate-in fade-in zoom-in-95 duration-200">
            <button
                onClick={() => toggleKpi(id)}
                className="absolute top-2 right-2 p-1 rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-all duration-200 z-10"
            >
                <X className="w-3 h-3" />
            </button>
            <div className="flex items-center gap-2 mb-2">
                <div className={`p-1.5 rounded-lg ${color} bg-opacity-10 dark:bg-opacity-20`}>
                    <Icon className={`w-3.5 h-3.5 ${color.replace('bg-', 'text-')}`} />
                </div>
                <h3 className="text-[11px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider truncate mr-4">{title}</h3>
            </div>
            <div className="text-xl font-black text-neutral-900 dark:text-neutral-100 truncate">
                {isCurrency ? formatCurrency(value, currencySymbol) : `${(value || 0).toFixed(precision)}${unit}`}
            </div>
        </div>
    );

    const calculateDailyMetrics = (dayTxs: any[] = []) => {
        let revenue = 0, expenses = 0, grossSales = 0, discounts = 0, returns = 0, adSpend = 0;
        let shippingRevenue = 0, transactionFees = 0;
        const orderIds = new Set<string>();
        const customerIds = new Set<string>();

        dayTxs.forEach(tx => {
            const amount = Number(tx.amount) || 0;
            const cat = (tx.category || '').toLowerCase();
            if (tx.type === 'REVENUE') {
                revenue += amount;
                if (cat === 'product_sale' || cat === 'sale') {
                    grossSales += amount;
                    if (tx.actor?.act) orderIds.add(`${tx.channelId}_${tx.actor.act}`);
                    if (tx.actor?.id) customerIds.add(`${tx.channelId}_${tx.actor.id}`);
                } else if (cat === 'shipping') {
                    shippingRevenue += amount;
                }
            } else if (tx.type === 'EXPENSE') {
                const abs = Math.abs(amount);
                expenses += abs;
                if (cat === 'ad_spend' || cat === 'advertising' || cat === 'marketing') adSpend += abs;
                else if (cat === 'discount') discounts += abs;
                else if (cat === 'product_return' || cat === 'partial_return' || cat === 'net_return') returns += abs;
                else if (cat === 'transaction_fee' || cat === 'processing_fee') transactionFees += abs;
            }
        });

        const orders = orderIds.size;
        const customers = customerIds.size;
        const net = revenue - expenses;

        return {
            grossSales,
            netSales: grossSales - discounts - returns,
            revenue,
            expenses,
            net,
            margin: revenue > 0 ? (net / revenue) * 100 : 0,
            orders,
            aov: orders > 0 ? grossSales / orders : 0,
            discountRate: grossSales > 0 ? (discounts / grossSales) * 100 : 0,
            returnRate: grossSales > 0 ? (returns / grossSales) * 100 : 0,
            shippingRevenue,
            transactionFees,
            txFeePercent: revenue > 0 ? (transactionFees / revenue) * 100 : 0,
            customers,
            avgRevenuePerCustomer: customers > 0 ? revenue / customers : 0,
            roas: adSpend > 0 ? revenue / adSpend : 0,
            cac: customers > 0 ? adSpend / customers : 0,
            adSpendPercent: revenue > 0 ? (adSpend / revenue) * 100 : 0,
        };
    };

    const { dailyMetricsMap, periodTotals } = useMemo(() => {
        const map: Record<string, any> = {};
        activePeriod.days.forEach(d => {
            const dateStr = format(d, 'yyyy-MM-dd');
            map[dateStr] = calculateDailyMetrics(tableData[dateStr] || []);
        });

        const allPeriodTxs = activePeriod.days.flatMap(d => tableData[format(d, 'yyyy-MM-dd')] || []);
        const totals = calculateDailyMetrics(allPeriodTxs);

        return { dailyMetricsMap: map, periodTotals: totals };
    }, [activePeriod, tableData]);

    const kpiRowOrder = useMemo(() => {
        const order = [
            'grossSales', 'revenue', 'expenses', 'net', 'margin', 'orders', 'aov',
            'discountRate', 'returnRate', 'shippingRevenue', 'transactionFees',
            'txFeePercent', 'customers', 'avgRevenuePerCustomer', 'roas', 'cac',
            'adSpendPercent', 'netSales'
        ];
        return order.filter(kpi => visibleKpis.includes(kpi));
    }, [visibleKpis]);

    return (
        <div className="h-full flex flex-col bg-white dark:bg-neutral-900 overflow-hidden relative">
            <div className="flex-1 overflow-hidden relative flex flex-col">
                <div
                    className="flex-1 overflow-y-auto fade-scrollbar px-2 lg:px-4"
                    onScroll={(e) => {
                        const container = e.currentTarget;
                        container.classList.add('scrolling');
                        const existingTimeout = (container as any).dataset.scrollTimeout;
                        if (existingTimeout) clearTimeout(parseInt(existingTimeout));
                        const timeoutId = setTimeout(() => {
                            container.classList.remove('scrolling');
                        }, 1000);
                        (container as any).dataset.scrollTimeout = timeoutId.toString();
                    }}
                >
                    <div className="pt-2 lg:pt-4 space-y-4 lg:space-y-6 pb-24">
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
                        {visibleKpis.map(key => {
                            const config = SUPPORTED_KPIS[key];
                            if (!config) return null;
                            return (
                                <KPICard
                                    key={key}
                                    id={key}
                                    title={config.title}
                                    value={metrics[key as keyof typeof metrics]}
                                    unit={config.unit}
                                    icon={config.icon}
                                    color={config.color}
                                    isCurrency={config.isCurrency}
                                    precision={config.precision}
                                />
                            );
                        })}

                        {visibleKpis.length < Object.keys(SUPPORTED_KPIS).length && (
                            <div className="relative">
                                <button
                                    ref={addButtonRef}
                                    onClick={() => {
                                        if (!isMenuOpen && addButtonRef.current) {
                                            const rect = addButtonRef.current.getBoundingClientRect();
                                            const spaceRight = window.innerWidth - rect.left;
                                            setMenuAlign(spaceRight < 270 ? 'right' : 'left');
                                        }
                                        setIsMenuOpen(!isMenuOpen);
                                    }}
                                    className={`w-full h-full min-h-[82px] rounded-xl border-2 border-dashed flex items-center justify-center transition-all duration-300 ${isMenuOpen
                                        ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/20 text-indigo-500/80 shadow-inner'
                                        : 'border-neutral-200 dark:border-neutral-800 hover:border-indigo-400/50 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 text-neutral-400 hover:text-indigo-400/80'
                                        }`}
                                >
                                    <Plus className="w-6 h-6 stroke-[1.5]" />
                                </button>
                                {isMenuOpen && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => setIsMenuOpen(false)} />
                                        <div
                                            ref={menuRef}
                                            className={`absolute top-full mt-2 ${menuAlign === 'left' ? 'left-0' : 'right-0'} z-50 w-[260px] bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-[0_10px_40px_rgba(0,0,0,0.15)] p-1 flex flex-col overflow-visible animate-in fade-in zoom-in-98 duration-100 origin-top`}
                                        >
                                            <div className="max-h-[400px] overflow-y-auto fade-scrollbar py-1">
                                                {Object.keys(SUPPORTED_KPIS).filter(key => !visibleKpis.includes(key)).map(key => {
                                                    const config = SUPPORTED_KPIS[key];
                                                    return (
                                                        <div key={key} className="px-1">
                                                            <button
                                                                onMouseEnter={(e) => {
                                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                                    const menuRect = menuRef.current?.getBoundingClientRect();
                                                                    if (menuRect) {
                                                                        setHoverOffset(rect.top - menuRect.top);
                                                                        setHoverSide(window.innerWidth - menuRect.right < 300 ? 'left' : 'right');
                                                                    }
                                                                    setHoveredKpi(key);
                                                                }}
                                                                onMouseLeave={() => setHoveredKpi(null)}
                                                                onClick={() => { toggleKpi(key); setIsMenuOpen(false); setHoveredKpi(null); }}
                                                                className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center gap-3 transition-colors group/btn"
                                                            >
                                                                <div className={`p-1.5 rounded-lg ${config.color} bg-opacity-10 group-hover/btn:bg-opacity-20 transition-all`}>
                                                                    <config.icon className={`w-3.5 h-3.5 ${config.color.replace('bg-', 'text-')}`} />
                                                                </div>
                                                                <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 truncate">{config.title}</span>
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {hoveredKpi && (
                                                <div
                                                    style={{ top: hoverOffset }}
                                                    className={`absolute ${hoverSide === 'right' ? 'left-[calc(100%+8px)]' : 'right-[calc(100%+8px)]'} w-72 p-5 bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-[20px_0_50px_rgba(0,0,0,0.1)] dark:shadow-[20px_0_50px_rgba(0,0,0,0.3)] z-[70] animate-in fade-in ${hoverSide === 'right' ? 'slide-in-from-left-1' : 'slide-in-from-right-1'} duration-150 pointer-events-none`}
                                                >
                                                    <div className="flex items-center gap-2.5 mb-3">
                                                        <div className={`w-1 h-3 rounded-full ${SUPPORTED_KPIS[hoveredKpi].color}`} />
                                                        <h4 className="font-bold text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500">{SUPPORTED_KPIS[hoveredKpi].title}</h4>
                                                    </div>
                                                    <p className="text-[13px] text-neutral-600 dark:text-neutral-300 leading-relaxed font-medium">{SUPPORTED_KPIS[hoveredKpi].description}</p>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                        {/* AI Insight Card - Order 1 on mobile, 2 on desktop */}
                        <div className="order-1 lg:order-2 lg:col-span-4 hidden lg:flex flex-col space-y-6">
                            <AIVerdictModule 
                                pageId={activePageId || ''} 
                                boardId={boardId || ''} 
                            />
                        </div>

                        {/* KPI Table - Order 2 on mobile, 1 on desktop */}
                        <div className="order-2 lg:order-1 lg:col-span-8 flex flex-col bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm overflow-hidden min-h-[500px]">
                            <div className="flex-1 overflow-auto relative">
                                <table className="w-full text-left border-collapse min-w-max">
                                    <thead className="sticky top-0 z-20 bg-white dark:bg-neutral-900 shadow-[0_1px_0_0_#f0f0f0] dark:shadow-[0_1px_0_0_#262626]">
                                        <tr>
                                            <th className="px-5 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 sticky left-0 bg-white dark:bg-neutral-900 shadow-[1px_0_0_0_#f0f0f0] dark:shadow-[1px_0_0_0_#262626] z-30">{t('pages.performance.kpiMetrics')}</th>
                                            {activePeriod.days.map(d => (
                                                <th key={format(d, 'yyyy-MM-dd')} className="px-5 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 text-right min-w-[100px] border-l border-neutral-50 dark:border-neutral-800/50">
                                                    {format(d, 'MMM dd', { locale: currentLocale })}
                                                    <div className="text-[10px] text-neutral-400 dark:text-neutral-500 font-normal mt-0.5">{format(d, 'EEE', { locale: currentLocale })}</div>
                                                </th>
                                            ))}
                                            <th className="px-4 md:px-6 py-3 text-[10px] md:text-xs font-black uppercase tracking-normal md:tracking-widest text-accent-primary text-right bg-white dark:bg-neutral-900 sticky right-0 z-30 shadow-[-1px_0_0_0_#f0f0f0] dark:shadow-[-1px_0_0_0_#262626]">
                                                <span className="md:hidden">{t('pages.performance.total')}</span>
                                                <span className="hidden md:inline">{t('pages.performance.periodTotal')}</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-neutral-900 divide-y divide-neutral-100 dark:divide-neutral-800">
                                        {kpiRowOrder.map(key => {
                                            const config = SUPPORTED_KPIS[key];
                                            return (
                                                <tr key={key} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
                                                    <td className="px-5 py-2 text-xs font-medium text-neutral-700 dark:text-neutral-300 sticky left-0 bg-white dark:bg-neutral-900 shadow-[1px_0_0_0_#f0f0f0] dark:shadow-[1px_0_0_0_#262626] z-10">
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-1.5 h-1.5 rounded-full ${config.color}`} />
                                                            {config.title}
                                                        </div>
                                                    </td>
                                                    {activePeriod.days.map(d => {
                                                        const dateStr = format(d, 'yyyy-MM-dd');
                                                        const dData = dailyMetricsMap[dateStr] || {};
                                                        const val = dData[key] ?? 0;
                                                        return (
                                                            <td key={dateStr} className="px-5 py-2 text-xs text-right whitespace-nowrap text-neutral-600 dark:text-neutral-400 border-l border-neutral-50 dark:border-neutral-800/50">
                                                                {val === 0 ? <span className="text-neutral-300 dark:text-neutral-700">-</span> :
                                                                    config.isCurrency ? formatCurrency(val, currencySymbol) : `${val.toFixed(config.precision ?? 2)}${config.unit ?? ''}`}
                                                            </td>
                                                        );
                                                    })}
                                                    <td className="px-4 md:px-6 py-2 text-xs text-right bg-white dark:bg-neutral-900 sticky right-0 z-10 shadow-[-1px_0_0_0_#f0f0f0] dark:shadow-[-1px_0_0_0_#262626] group-hover:bg-neutral-50 dark:group-hover:bg-neutral-800/80">
                                                        <div className="font-black text-accent-primary">
                                                            {(periodTotals as any)[key] === 0 ? '-' : (config.isCurrency ? formatCurrency((periodTotals as any)[key], currencySymbol) : `${(periodTotals as any)[key].toFixed(config.precision ?? 2)}${config.unit ?? ''}`)}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

                <BoardBottomDrawer
                    isOpen={isAiMobileExpanded}
                    onClose={() => setIsAiMobileExpanded(false)}
                    onOpen={() => setIsAiMobileExpanded(true)}
                    height="100%"
                    initialHeight="84px"
                    showTints={true}
                    className="lg:hidden"
                >
                    <div className="cursor-pointer" onClick={() => !isAiMobileExpanded && setIsAiMobileExpanded(true)}>
                        <h4 className={`font-bold text-text-primary mb-2 transition-all ${isAiMobileExpanded ? 'text-lg' : 'text-sm'}`}>
                            {t('verdicts.labels.aiInsight')}
                        </h4>

                        {isAiMobileExpanded && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.2 }}
                                className="pt-2"
                            >
                                <AIVerdictModule 
                                    pageId={activePageId || ''} 
                                    boardId={boardId || ''} 
                                />
                            </motion.div>
                        )}
                    </div>
                </BoardBottomDrawer>
            </div>

            <div className="px-4 py-2 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 flex items-center justify-between shrink-0 z-20">
                <div className="flex-1 overflow-x-auto scrollbar-hide flex items-center gap-2 mr-6">
                    {periods.map((p, idx) => (
                        <button
                            key={idx}
                            onClick={() => setSelectedPeriodIndex(idx)}
                            className={`whitespace-nowrap px-4 py-1.5 text-xs font-medium rounded-full transition-all flex-shrink-0 border ${selectedPeriodIndex === idx
                                ? 'bg-neutral-800 text-white border-neutral-800 dark:bg-white dark:text-neutral-900 dark:border-white shadow-sm'
                                : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-700 dark:hover:border-neutral-600'
                                }`}
                        >
                            {p.title}
                        </button>
                    ))}
                </div>

                <div className="flex bg-neutral-100 dark:bg-neutral-800 p-0.5 rounded-lg border border-neutral-200 dark:border-neutral-700 shrink-0">
                    <button
                        className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md transition-all ${viewMode === 'all' ? 'bg-white dark:bg-neutral-700 shadow-sm text-neutral-800 dark:text-neutral-200' : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'}`}
                        onClick={() => { setViewMode('all'); setSelectedPeriodIndex(0); }}
                    >
                        {t('charts.components.all', 'All')}
                    </button>
                    <button
                        className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md transition-all ${viewMode === 'weeks' ? 'bg-white dark:bg-neutral-700 shadow-sm text-neutral-800 dark:text-neutral-200' : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'}`}
                        onClick={() => { setViewMode('weeks'); setSelectedPeriodIndex(0); }}
                    >
                        {t('charts.components.weeks')}
                    </button>
                    <button
                        className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md transition-all ${viewMode === 'months' ? 'bg-white dark:bg-neutral-700 shadow-sm text-neutral-800 dark:text-neutral-200' : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'}`}
                        onClick={() => { setViewMode('months'); setSelectedPeriodIndex(0); }}
                    >
                        {t('charts.components.months')}
                    </button>
                </div>
            </div>

        </div>
    );
};
